import { CountryCode } from '~/enums';
import { formatAmount } from '~/helpers/formatters.helper';
import React, { useMemo, useState } from 'react';
import useTranslate, { useCampaignTypeTranslate } from '~/hooks/useTranslate';
import { useSelector } from 'react-redux';
import { RootState } from '~/store';
import Select from '~/components/shared/Select/Select';
import useDateFormatter from '~/hooks/useDateFormatter';
import { Body, Container, H6, Stack } from '@qred/components-library';
import { ComboboxItem } from '@qred/components-library/dist/lib/next/components/Combobox/Combobox.types';

import { SvgSrc } from '~/components/shared/Icon/Icon';
import Table, {
  CellData,
} from '~/components/shared/PaymentCalculatorTable/PaymentCalculatorTable';
import Spinner from '~/components/shared/Spinner/SpinnerCircular';
import { useGetPaymentPlanQuery } from '~/store/apis/endpoints/onboardingApi/onboarding.api';

interface PaymentCalculatorModalProps {
  applicationUuid: string;
  amount: number;
  initialLoanPeriod: number;
}

const getSelectedLoanPeriodOption = (
  loanPeriodOptions: ComboboxItem[],
  loanPeriod: number
): ComboboxItem => {
  const selectedOption = loanPeriodOptions.find(
    (option) => Number(option.value) === loanPeriod
  );

  return selectedOption || loanPeriodOptions[loanPeriodOptions.length - 1];
};

const PaymentCalculatorModal: React.FC<PaymentCalculatorModalProps> = ({
  applicationUuid,
  amount,
  initialLoanPeriod,
}) => {
  const { market } = useSelector((state: RootState) => state.intl);
  const { form, overview } = useSelector((state: RootState) => state.loanOffer);
  const formatDate = useDateFormatter();
  const translate = useTranslate();

  const [loanPeriod, setLoanPeriod] = useState(initialLoanPeriod);

  const { data: paymentPlanData, isFetching, isError } = useGetPaymentPlanQuery(
    {
      applicationUuid,
      loanAmount: amount,
      loanTerm: loanPeriod,
    }
  );

  // Track the min/max loan period
  const minLoanPeriod = overview.minTerm;
  const maxLoanPeriod = form.loanPeriod;

  // Transform payment plan data into table format
  const [tableData, footers] = useMemo(() => {
    // If we don't have payment plan data, return empty
    if (!paymentPlanData) {
      return [[], []];
    }

    const _tableData: Array<Array<CellData>> = [];
    let totalAmortization = 0;
    let totalMonthlyFee = 0;
    let totalToPay = 0;

    // Transform API response to table data, limiting to maxLoanPeriod
    paymentPlanData.installments
      .slice(0, maxLoanPeriod)
      .forEach((installment) => {
        totalAmortization += installment.amortization;
        totalMonthlyFee += installment.monthlyFee;
        totalToPay += installment.totalAmount;

        const rowData: Array<CellData> = [
          { value: installment.installment },
          { value: formatDate(new Date(installment.dueDate)) },
          { value: formatAmount(installment.amortization) },
          { value: formatAmount(installment.monthlyFee) },
          {
            value: formatAmount(installment.totalAmount),
            isFullWidthCell: true,
          },
        ];

        _tableData.push(rowData);
      });

    const _footers = [
      { value: '', isEmptyCell: true },
      { value: translate('Total') as string, isTotalLabel: true },
      { value: formatAmount(totalAmortization) },
      { value: formatAmount(totalMonthlyFee) },
      { value: formatAmount(totalToPay) },
    ];

    return [_tableData, _footers];
  }, [paymentPlanData, formatDate, translate, maxLoanPeriod]);

  const loanPeriodOptions = useMemo(() => {
    const values: ComboboxItem[] = [];
    for (let index = minLoanPeriod; index <= maxLoanPeriod; index += 1) {
      values.push({
        label: translate('PaymentCalculator.DropdownItem', {
          itemCount: index,
        }) as string,
        value: String(index),
      });
    }

    return values;
  }, [minLoanPeriod, maxLoanPeriod, translate]);

  const headers = [
    { value: translate('Installment') as string, iconSrc: SvgSrc.ChartPie },
    { value: translate('DueDate') as string, iconSrc: SvgSrc.Clock },
    { value: translate('Amortization') as string, iconSrc: SvgSrc.ChartArea },
    { value: translate('MonthlyFee') as string, iconSrc: SvgSrc.Coins },
    { value: translate('ToPay') as string, iconSrc: SvgSrc.MoneyBillAlt },
  ];

  const campaignTypeTranslate = useCampaignTypeTranslate(
    paymentPlanData?.campaign?.type || null,
    paymentPlanData?.campaign?.principalFreeMonths,
    paymentPlanData?.campaign?.interestRateDiscount
  );

  // Show error state for initial load failures
  if (isError && !paymentPlanData) {
    return <Body size="lg">Error: Failed to fetch payment plan</Body>;
  }

  // Show full-window loading state
  if (isFetching) {
    return (
      <Stack align="center" justify="center" mih={500} w="100%">
        <Spinner />
      </Stack>
    );
  }

  return (
    <Stack spacing="sm" mt={24}>
      {paymentPlanData?.campaign?.type && (
        <H6>
          {translate('PaymentCalculator.CampaignApplied')} -{' '}
          {campaignTypeTranslate}
        </H6>
      )}
      {market !== CountryCode.NO && (
        <Body size="lg">
          {translate('PaymentCalculator.HeaderExplanation')}
        </Body>
      )}
      <Container m={0} maw={'22em'}>
        <Select
          name="LoanPeriod"
          options={loanPeriodOptions}
          onChange={(v) => v?.value && setLoanPeriod(Number(v.value))}
          value={getSelectedLoanPeriodOption(loanPeriodOptions, loanPeriod)}
        />
      </Container>
      <Stack spacing="sm" pos="relative" mih={400}>
        {!paymentPlanData ? (
          <Body size="lg">
            {translate('PaymentCalculator.NoPaymentPlanAvailable')}
          </Body>
        ) : (
          <>
            <Table
              center
              data={tableData}
              headers={headers}
              footers={footers}
            />
            <Body size="sm">
              {translate('PaymentCalculator.FooterExplanation')}
            </Body>
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default PaymentCalculatorModal;
